import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useAdoption, useSetAdoption } from '@/api/adoption'
import type { Adoption, AdoptionBody } from '@/api/adoption'
import { useIdempotencyKey } from '@/api/idempotency'
import type { ApiError } from '@/api/me'
import { useSetOnboarding } from '@/api/onboarding'
import type { OnboardingTrack } from '@/api/onboarding'
import { useWorkspace } from '@/api/workspace'
import { FullPageMessage } from '@/components/full-page-message'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

// The Welcome screen (09 Onboarding, #229): the Owner picks an Onboarding track and sets the
// Adoption date, then lands on Beranda. Full page, outside the shell (see the workspace layout).
export const Route = createFileRoute('/w/$slug/welcome')({
  component: Welcome,
})

const TRACKS: OnboardingTrack[] = ['from_scratch', 'existing_stock']

function Welcome() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  // Already loaded by the workspace layout.
  const workspace = useWorkspace(slug)
  const adoption = useAdoption(slug)

  // Staff never see it, and a finished onboarding has nothing to choose any more.
  if (workspace.data?.role !== 'owner' || workspace.data.onboarding.onboarding_completed_at) {
    return <Navigate to="/w/$slug" params={{ slug }} replace />
  }
  if (adoption.isPending) {
    return (
      <main className="mx-auto flex min-h-svh w-full max-w-xl flex-col justify-center gap-6 p-4 sm:p-6">
        <LoadingSkeleton variant="form" rows={3} />
      </main>
    )
  }
  if (adoption.isError || !adoption.data) return <FullPageMessage title={t('errors.generic')} />

  const track = workspace.data.onboarding.onboarding_track as OnboardingTrack | null

  return (
    <main className="mx-auto flex min-h-svh w-full max-w-xl flex-col justify-center gap-6 p-4 sm:p-6">
      <header className="flex flex-col gap-2">
        <h1 className="font-heading text-2xl font-semibold">{t('onboarding.welcome.title')}</h1>
        <p className="text-muted-foreground">{t('onboarding.welcome.intro')}</p>
      </header>
      <WelcomeForm slug={slug} adoption={adoption.data} track={track} />
    </main>
  )
}

function WelcomeForm({ slug, adoption, track }: { slug: string; adoption: Adoption; track: OnboardingTrack | null }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const setAdoption = useSetAdoption()
  const setOnboarding = useSetOnboarding()
  const idempotency = useIdempotencyKey()
  const [requestError, setRequestError] = useState('')
  const locked = adoption.locked || !adoption.can.update

  const formSchema = z.object({
    track: z.enum(['from_scratch', 'existing_stock'], { message: t('onboarding.welcome.trackRequired') }),
    adoptedOn: locked
      ? z.string()
      : z
          .string()
          .min(1, t('onboarding.welcome.adoptionRequired'))
          .refine((value) => value <= adoption.today, { message: t('onboarding.welcome.inFuture') }),
  })
  type FormValues = z.infer<typeof formSchema>
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { track: track ?? undefined, adoptedOn: adoption.adopted_on ?? adoption.today },
  })
  const pending = setAdoption.isPending || setOnboarding.isPending

  const onSubmit = form.handleSubmit(async ({ track, adoptedOn }) => {
    setRequestError('')
    try {
      // The Adoption date goes through its own endpoint and rules (A-11); skipped when locked or unchanged.
      if (!locked && adoptedOn !== adoption.adopted_on) {
        const body: AdoptionBody = { adoption: { adopted_on: adoptedOn } }
        try {
          await setAdoption.mutateAsync({ params: { path: { slug }, header: idempotency.headerFor(body) }, body })
          idempotency.settle()
        } catch (error) {
          idempotency.settle(error)
          throw error
        }
      }
      await setOnboarding.mutateAsync({ params: { path: { slug } }, body: { onboarding: { onboarding_track: track } } })
      await navigate({ to: '/w/$slug', params: { slug } })
    } catch (error) {
      setRequestError(errorMessage(t, error))
    }
  })

  const selected = useWatch({ control: form.control, name: 'track' })
  const trackError = form.formState.errors.track?.message ?? ''
  const dateError = form.formState.errors.adoptedOn?.message ?? ''

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-6" noValidate>
      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {requestError}
      </p>

      <fieldset className="flex flex-col gap-3" aria-describedby="track-error">
        <legend className="mb-3 text-sm font-medium">{t('onboarding.welcome.trackLegend')}</legend>
        {TRACKS.map((value) => (
          <label
            key={value}
            className={cn(
              'flex cursor-pointer gap-3 rounded-lg border p-4 transition-colors has-focus-visible:ring-2 has-focus-visible:ring-ring',
              selected === value ? 'border-primary bg-primary/5' : 'hover:bg-muted/50',
            )}
          >
            <input type="radio" value={value} className="mt-1 size-4 accent-primary" {...form.register('track')} />
            <span className="flex flex-col gap-1">
              <span className="font-medium">{t(`onboarding.welcome.tracks.${value}.title`)}</span>
              <span className="text-sm text-muted-foreground">{t(`onboarding.welcome.tracks.${value}.body`)}</span>
            </span>
          </label>
        ))}
        <p id="track-error" role="alert" className="text-sm text-destructive empty:hidden">
          {trackError}
        </p>
      </fieldset>

      <div className="flex flex-col gap-2">
        <Label htmlFor="adopted-on">{t('onboarding.welcome.adoptionLabel')}</Label>
        {locked ? (
          <>
            <p id="adopted-on" className="text-base">
              {adoption.adopted_on ? formatDate(adoption.adopted_on) : ''}
            </p>
            <p className="text-sm text-muted-foreground">{t('onboarding.welcome.adoptionLocked')}</p>
          </>
        ) : (
          <>
            <Input
              id="adopted-on"
              type="date"
              className="h-11"
              max={adoption.today}
              aria-invalid={Boolean(dateError)}
              aria-describedby="adopted-on-hint adopted-on-error"
              {...form.register('adoptedOn')}
            />
            <p id="adopted-on-hint" className="text-sm text-muted-foreground">
              {t(
                selected === 'existing_stock'
                  ? 'onboarding.welcome.adoptionHintExistingStock'
                  : 'onboarding.welcome.adoptionHint',
              )}
            </p>
            <p id="adopted-on-error" role="alert" className="text-sm text-destructive empty:hidden">
              {dateError}
            </p>
          </>
        )}
      </div>

      <Button type="submit" className="h-11" disabled={pending}>
        {t(pending ? 'onboarding.welcome.starting' : 'onboarding.welcome.start')}
      </Button>
    </form>
  )
}

// 2026-09-01 → "1 September 2026". The date has no time, so it is formatted as UTC.
function formatDate(iso: string) {
  return new Intl.DateTimeFormat('id-ID', { dateStyle: 'long', timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`))
}

// Server errors are translated by their stable key (ARCHITECTURE API errors).
function errorMessage(t: (key: string) => string, error: unknown) {
  switch ((error as ApiError | undefined)?.error?.key) {
    case 'adoption_date_locked':
      return t('onboarding.welcome.adoptionLocked')
    case 'validation_failed':
      return t('settings.adoption.invalid')
    default:
      return t('errors.generic')
  }
}
